<!--
  Object component of one pc.CallProtocol.

  It renders in the place the protocol list occupied a moment ago — the host swaps the two, it does
  not stack them. This component therefore brings its own toolbar (back, status, capture switch) but
  no overlay of its own and must never grow one.

  Round-based LLM dialog — design: projects/pc_call_obc/REQUIREMENT.md §8.3, §9.
-->

<!--
  The toolbar sits OUTSIDE the "@if (initialized && dto)" guard on purpose.

  postRefreshObjectError blanks dto and initialized, so everything inside that guard disappears when
  a load or the initial save fails. With the back button in there, a failure left an empty panel with
  no way out: the host stays in its edit state and <mvs-object> forwards no load-error output, so the
  advisor had to reload the page. "Zurück" must therefore always be reachable.
-->
<p-toolbar styleClass="mb-3 py-2">

    <div class="p-toolbar-group-start flex align-items-center gap-2">

        <!-- Available at all times, including in the middle of an AI dialog. The host navigates. -->
        <p-button
                icon="fa-regular fa-arrow-left"
                label="Zurück"
                styleClass="p-button-text p-button-secondary"
                (onClick)="onBack()">
        </p-button>

        @if (initialized && dto) {
            @if (isCompleted()) {
                <p-tag severity="success" value="Abgeschlossen" icon="fa-regular fa-check"></p-tag>
            } @else {
                <p-tag severity="info" value="In Bearbeitung" icon="fa-regular fa-pen"></p-tag>
            }
        }

    </div>

    <div class="p-toolbar-group-end flex align-items-center gap-2">

        @if (initialized && dto) {

            <!--
              The capture switch. Offered only where the call type carries an AI dialog type, and
              switchable at any time — in edit as well, not once per protocol.
            -->
            @if (aiDialogTypeId) {
                <p-selectButton
                        [options]="captureModeOptions"
                        [ngModel]="captureMode"
                        (onChange)="onCaptureModeChange($event.value)"
                        optionLabel="label"
                        optionValue="value"
                        [allowEmpty]="false"
                        ariaLabel="Erfassungsart des Protokolls umschalten">

                    <!--
                      Icons for the two capture paths. Rendered from the option VALUE rather than from a
                      field on the option objects, so this stays inside this template: the option list in
                      the class is next to the data-loss fix and is deliberately left untouched.

                      "KI" gets fa-wand-magic-sparkles, the marker Alpha already uses for its assistant
                      features — recognisability beats novelty here, and the alternative in-house glyph
                      fa-comments reads as "comments", which would be wrong twice over: the OTHER path is
                      the one with the comment editor. (pi pi-sparkles is the launchpad's PrimeIcons
                      variant of the same idea and is not used here — this application draws from Font
                      Awesome.)

                      "Eingabe" gets one too, and not for symmetry alone: a pair in which only one button
                      is decorated reads as a defect, and the keyboard glyph is what actually names the
                      contrast the switch is about — write it yourself, or have it written for you.
                    -->
                    <ng-template #item let-option>
                        <span class="flex align-items-center gap-2">
                            <i [class]="option.value === 'ai'
                                    ? 'fa-solid fa-wand-magic-sparkles'
                                    : 'fa-regular fa-keyboard'"></i>
                            <span>{{ option.label }}</span>
                        </span>
                    </ng-template>

                </p-selectButton>
            }

            <!-- Exactly two status actions. The generic status picker is hidden from the form. -->
            @if (isCompleted()) {
                <p-button
                        icon="fa-regular fa-lock-open"
                        label="Wieder öffnen"
                        styleClass="p-button-text"
                        [disabled]="statusChanging"
                        (onClick)="onReopenProtocol()">
                </p-button>
            } @else {
                <!--
                  Blocked while the form holds unsaved input: completing switches the view to
                  read-only, so an unsaved edit would be silently dropped behind a lock.
                -->
                <p-button
                        icon="fa-regular fa-check"
                        label="Abschließen"
                        [disabled]="statusChanging || formDirty"
                        (onClick)="onCompleteProtocol()">
                </p-button>
            }

        }

    </div>

</p-toolbar>

@if (creating) {

    <!-- The initial save. From the moment it returns there is only edit. -->
    <div class="p-3">
        <p-progressBar mode="indeterminate" [style]="{'height': '4px'}"></p-progressBar>
        <div class="text-sm text-color-secondary mt-2">Protokoll wird angelegt …</div>
    </div>

}

@if (loadError) {

    <!-- Something to read and a way out, instead of the empty panel this used to be. -->
    <p-message
            severity="error"
            [text]="loadErrorMessage"
            styleClass="w-full mb-3">
    </p-message>

}

@if (initialized && dto) {

    @if (isCompleted()) {
        <p-message
                severity="info"
                text="Das Protokoll ist abgeschlossen und wird nur angezeigt. Über „Wieder öffnen“ kann es erneut bearbeitet werden."
                styleClass="w-full mb-3">
        </p-message>
    } @else if (formDirty) {
        <p-message
                severity="warn"
                text="Es gibt ungespeicherte Änderungen. Bitte zuerst speichern — danach lässt sich das Protokoll abschließen."
                styleClass="w-full mb-3">
        </p-message>
    }

    @if (captureMode === 'ai' && aiDialogTypeId) {

        <!--
          Same place and same width as the form it replaces.

          THE HEIGHT IS BOUND HERE, AND ONLY HERE. mvs-ai-dialog is a fixed header + a scrolling
          history + a fixed composer, which needs a bounded height — and it deliberately refuses to
          choose one for itself (decision D-9, §8.3: the presentation form belongs to the caller). It
          declares height:100% + flex column + min-height:0 and FILLS whatever it is given. This host
          is therefore the only place that says how tall it is; nothing else about the dialog is
          configured from here, and no layout may move into it.

          WHY mvs-review-workbench, and why with ONE pane. The height that is correct here cannot be
          written as a constant: the chrome above the dialog is not constant (the toolbar, plus the
          "abgeschlossen" / "ungespeicherte Änderungen" banners, plus the shell's optional breadcrumb),
          so every `calc(100vh - Npx)` would be wrong in at least one of those states — the reason is
          spelled out in mvs-review-workbench.component.ts:31-56. That component owns the ONLY in-house
          implementation of the measured height contract (clientHeight - host top - gap, re-measured
          over the whole ancestor chain, bound via [style.height], never in a stylesheet). Using it
          here buys that contract, plus the .rw-col flex column with the min-height:0 reset, without
          copying sixty lines of measurement machinery — including its self-feeding-loop guard — into
          this component. `panes = 1` is a documented, supported mode of that component (see its
          `panes` input) and renders the single pane full width with no gutter; what is borrowed is its
          height frame, not its worklist semantics. viewportBottomGapRem keeps a rem-sized breathing
          space below the dialog that follows the app's UI scale instead of freezing at one zoom level.

          readOnly on a completed protocol: the dialog's write path is find-or-create, so opening it
          in write mode over an already final text would start a SECOND dialog (§9 rule 11).
        -->
        <mvs-review-workbench
                [panes]="1"
                [autoFitViewport]="true"
                [viewportBottomGapRem]="1">

            <ng-template #workbenchList>
                <mvs-ai-dialog
                        [dialogTypeId]="aiDialogTypeId"
                        [objectId]="objectIdentifier.objectId"
                        [readOnly]="isCompleted()"
                        (onFinalized)="onAiDialogFinalized()">
                </mvs-ai-dialog>
            </ng-template>

        </mvs-review-workbench>

    } @else {

        <!--
          The metadata-driven protocol form with the comment editor. formControlOverwrite hides the
          generic status picker; readonly + hideDefaultActions turn a completed protocol into a pure
          display without a save action.

          onFormDirty is what tells this component that the advisor has typed something the database
          has not seen yet. Without it isComponentDirty() answered false for the whole lifetime of the
          component, and the completion action could lock away unsaved input.
        -->
        <mvs-crud-object
                objectType="pc.CallProtocol"
                [objectId]="objectIdentifier.objectId"
                [importObjectContextDto]="importObjectContext"
                [formControlOverwrite]="formControlOverwrite"
                [readonly]="isCompleted()"
                [hideDefaultActions]="isCompleted()"
                (onFormDirty)="onFormDirtyChange($event)"
                (onChangedObject)="onFormChangedObject($event)"
        ></mvs-crud-object>

    }

}
